import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { WorkTimeTemplate } from '../../types/workTimeTemplate';
import { useWorkTimeTemplateStore } from '../../store/workTimeTemplateStore';
import TemplatePreview from './TemplatePreview';

interface TemplateSelectorProps {
  selectedTemplateId?: string;
  onSelect: (template: WorkTimeTemplate | null) => void;
  disabled?: boolean;
  userId: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onSelect,
  disabled = false,
  userId,
}) => {
  const { templates, loading, error, fetchTemplates } = useWorkTimeTemplateStore();
  const [selected, setSelected] = useState<string | null>(selectedTemplateId || null);

  useEffect(() => {
    if (userId) {
      fetchTemplates(userId);
    }
  }, [userId, fetchTemplates]);

  useEffect(() => {
    if (selectedTemplateId) {
      setSelected(selectedTemplateId);
    }
  }, [selectedTemplateId]);

  const handleSelect = (templateId: string) => {
    if (disabled) return;
    
    setSelected(templateId);
    const template = templates.find(t => t.id === templateId);
    onSelect(template || null);
  };

  const selectedTemplate = templates.find(t => t.id === selected);

  return (
    <Box>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel id="template-select-label">근무 타임 템플릿</InputLabel>
        <Select
          labelId="template-select-label"
          value={selected || ''}
          onChange={(e) => handleSelect(e.target.value)}
          label="근무 타임 템플릿"
          disabled={disabled}
        >
          <MenuItem value="">
            <em>템플릿 선택 안 함</em>
          </MenuItem>
          {templates.map(template => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && templates.length === 0 && !error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          사용 가능한 템플릿이 없습니다. 일기월장 앱에서 템플릿을 생성해주세요.
        </Alert>
      )}

      {selectedTemplate && (
        <TemplatePreview template={selectedTemplate} />
      )}
    </Box>
  );
};

export default TemplateSelector;

